import { useEffect, useState } from "react";
import { Users, Eye, ShoppingBag, TrendingUp, DollarSign } from "lucide-react";
import { api } from "@/lib/api";

export const AnalyticsTab = () => {
    const [data, setData] = useState(null);

    useEffect(() => {
        api.get("/analytics").then((r) => setData(r.data)).catch(() => {});
    }, []);

    if (!data) {
        return <p className="font-serif text-lg italic text-clay" data-testid="analytics-loading">Counting visitors…</p>;
    }

    const cards = [
        { label: "Visitors", value: data.visitors, icon: Users, testid: "stat-visitors" },
        { label: "Page views", value: data.pageviews, icon: Eye, testid: "stat-pageviews" },
        { label: "Product views", value: data.product_views, icon: ShoppingBag, testid: "stat-product-views" },
        { label: "Sales", value: data.sales_count, icon: TrendingUp, testid: "stat-sales" },
        { label: "Revenue", value: `$${data.revenue.toFixed(2)}`, icon: DollarSign, testid: "stat-revenue" },
    ];

    const maxPv = Math.max(1, ...data.series.map((d) => d.pageviews));

    return (
        <div data-testid="analytics-tab">
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
                {cards.map((c) => (
                    <div key={c.label} className="border border-line bg-white p-5" data-testid={c.testid}>
                        <c.icon size={16} className="text-gold" />
                        <p className="mt-3 font-serif text-3xl font-light">{c.value}</p>
                        <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-clay">{c.label}</p>
                    </div>
                ))}
            </div>
            <div className="mt-8 border border-line bg-white p-6">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-clay">Last 14 days — page views</p>
                {data.series.length === 0 ? (
                    <p className="mt-6 font-serif text-lg italic text-clay">No traffic yet — share your link and watch this come alive.</p>
                ) : (
                    <div className="mt-6 flex h-40 items-end gap-2 border-b border-l border-line pl-2" data-testid="analytics-chart">
                        {data.series.map((d) => (
                            <div key={d.day} className="group flex flex-1 flex-col items-center justify-end" title={`${d.day}: ${d.pageviews} views, ${d.visitors} visitors`}>
                                <div
                                    className="w-full bg-gold/80 transition-colors group-hover:bg-gold"
                                    style={{ height: `${Math.max(4, (d.pageviews / maxPv) * 130)}px` }}
                                    data-testid={`chart-bar-${d.day}`}
                                />
                                <span className="mt-1 font-mono text-[8px] text-clay">{d.day.slice(5)}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-2">
                <div className="border border-line bg-white p-6" data-testid="top-products">
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-clay">Most viewed products</p>
                    {data.top_products.length === 0 ? (
                        <p className="mt-4 font-serif text-base italic text-clay">No product views yet.</p>
                    ) : (
                        data.top_products.map((p, i) => (
                            <div key={p.name} className="mt-4 flex items-center justify-between border-b border-line pb-3 last:border-0">
                                <span className="font-serif text-base"><span className="mr-3 font-mono text-xs text-gold">{i + 1}</span>{p.name}</span>
                                <span className="font-mono text-xs text-clay">{p.views} views</span>
                            </div>
                        ))
                    )}
                </div>
                <div className="border border-line bg-white p-6" data-testid="top-referrers">
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-clay">Where visitors come from</p>
                    {data.top_referrers.length === 0 ? (
                        <p className="mt-4 font-serif text-base italic text-clay">No visits yet.</p>
                    ) : (
                        data.top_referrers.map((r) => (
                            <div key={r.source} className="mt-4 flex items-center justify-between border-b border-line pb-3 last:border-0">
                                <span className="font-mono text-xs">{r.source}</span>
                                <span className="font-mono text-xs text-clay">{r.visits} visits</span>
                            </div>
                        ))
                    )}
                </div>
            </div>
            {data.recent_orders.length > 0 && (
                <div className="mt-8 border border-line bg-white p-6" data-testid="recent-sales">
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-clay">Recent sales</p>
                    {data.recent_orders.map((o) => (
                        <div key={o.id} className="mt-4 flex items-center justify-between border-b border-line pb-3 last:border-0">
                            <span className="font-serif text-base">{o.customer_name || "Customer"} <span className="font-mono text-[10px] uppercase text-clay">· {o.items} item{o.items === 1 ? "" : "s"}</span></span>
                            <span className="font-mono text-xs">${o.amount_total.toFixed(2)} · {new Date(o.created_at).toLocaleDateString()}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
